import React, { useEffect, useMemo, useState } from 'react';
import { showToast } from '../context/ToastHandle';
import { getAccountType } from '../context/accountUtils';

/**
 * Popup for adding a new board.
 * - Fixed-size preview that matches BoardView-new sizing logic.
 * - scrollable = !autoFit
 * - square_buttons = squareTiles
 */
// top of Popup.jsx signature
const Popup = ({ isOpen, onClose, onSubmit, mode = 'add', initialData = {}, availableBoards = [] }) => {

  const [boardName, setBoardName] = useState('');
  const [cols, setCols] = useState(8);
  const [rows, setRows] = useState(5);
  const [autoFit, setAutoFit] = useState(true); // true => non-scrollable (fits to height)
  const [squareTiles, setSquareTiles] = useState(true);
  const [theme, setTheme] = useState('default');
  const [isCoreBoard, setIsCoreBoard] = useState(false);
  const [coreDefaultBoardId, setCoreDefaultBoardId] = useState('');
  const isUnlimited = getAccountType() === 'unlimited';

  // Fixed preview frame (simulate a “viewport”)
  const PREVIEW_WIDTH = 360;
  const PREVIEW_HEIGHT = 252; // ~aspect of a tablet strip under header
  const GAP = 10;             // keep consistent with app
  const iRow = initialData.rows || 5;
  const iCol = initialData.columns || 8;
  const iTotalTiles = iRow * iCol;  
  // Reset defaults when opening
  useEffect(() => {
    if (isOpen) {
      setBoardName(initialData.name || '');
      setCols(initialData.columns || 8);
      setRows(initialData.rows || 5);
      setAutoFit(!initialData.scrollable ?? true);
      setSquareTiles(initialData.square_buttons ?? true);
      setTheme(initialData.theme || 'default');
      const initialCore = isUnlimited && Boolean(
        initialData.is_core_board === true ||
        initialData.is_core_board === 1 ||
        initialData.is_core_board === '1'
      );
      setIsCoreBoard(initialCore);
      setCoreDefaultBoardId(
        initialCore && initialData.core_default_board_id
          ? String(initialData.core_default_board_id)
          : ''
      );
    }
  }, [isOpen, isUnlimited]);

  useEffect(() => {
    if (!isCoreBoard) {
      setCoreDefaultBoardId('');
      return;
    }
    if (cols > 4) setCols(4);
    if (rows > 6) setRows(6);
  }, [isCoreBoard, cols, rows]);

  // === Same logic as BoardView-new (translated for a fixed preview box) ===
  const preview = useMemo(() => {
    const scrollable = !autoFit;
    const windowWidth = PREVIEW_WIDTH;
    const windowHeight = PREVIEW_HEIGHT;

    let tileWidth, tileHeight, allowScrollY;

    if (scrollable) {
      // Scrollable mode — fit width only, with a little extra side breathing room (+4 gaps)
      tileWidth = (windowWidth - GAP * (cols + 4)) / cols;
      tileHeight = squareTiles ? tileWidth : Math.max(tileWidth * 0.5, 48); // 80 * 0.6 for preview
      allowScrollY = true;
    } else {
      // Non-scrollable — fit to both width and height (mirror your calc)
      const maxByWidth = (windowWidth - GAP * (cols + 2)) / cols;
      const maxByHeight = (windowHeight - GAP * (rows + 2)) / rows;

      if (squareTiles) {
        tileWidth = tileHeight = Math.floor(Math.min(maxByWidth, maxByHeight));
      } else {
        tileWidth = maxByWidth;
        tileHeight = maxByHeight;
      }
      allowScrollY = false;
    }

    // Crisp rounding for preview
    tileWidth = Math.max(10, Math.round(tileWidth));
    tileHeight = Math.max(10, Math.round(tileHeight));

    return {
      TILE_WIDTH: tileWidth,
      TILE_HEIGHT: tileHeight,
      columns: cols,
      rows,
      allowScrollY,
      cTILE_GAP: GAP,
      rTILE_GAP: GAP,
      frameWidth: PREVIEW_WIDTH,
      frameHeight: PREVIEW_HEIGHT,
    };
  }, [cols, rows, autoFit, squareTiles]);

  // ❗️Guard AFTER hooks so hook order never changes between renders
  if (!isOpen) return null;

  const handleAdd = () => {
    if (!boardName.trim()){
      showToast('Please enter a board name.', 'error', false);
      return;
    };
    const grid_config = {
      columns: cols,
      rows,
    };
    onSubmit?.({
      name: boardName.trim(),
      grid_config,
      scrollable: !autoFit,
      square_buttons: squareTiles,
      theme,
      is_core_board: isUnlimited && isCoreBoard,
      core_default_board_id: isUnlimited && isCoreBoard && coreDefaultBoardId
        ? Number(coreDefaultBoardId)
        : null,
    });
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h3 style={{ marginTop: 0, textAlign: 'center' }}>
          {mode === 'edit' ? 'Edit Board Setup' : 'Add New Board'}
        </h3>

        {mode === 'edit' && (
          <p style={{fontWeight: 'bold', color: 'rgba(255, 13, 0, 1)'}}>
            Editing an existing board will affect how the board is displayed and will modify tiles and tile arrangements.
          </p>
        )}
        <div style={styles.wrapper}>
        <label style={styles.label1}>Board Name:</label>
        <input
          type="text"
          value={boardName}
          onChange={(e) => setBoardName(e.target.value)}
          placeholder="Enter board name"
          style={styles.input}
        />
        </div>

        {isUnlimited && (
          <div style={styles.coreSection}>
            <div style={styles.coreHeaderRow}>
              <label style={styles.coreToggleLabel}>
                <input
                  type="checkbox"
                  checked={isCoreBoard}
                  onChange={(e) => setIsCoreBoard(e.target.checked)}
                  style={styles.coreCheckbox}
                />
                <span>Make this a Core Board</span>
            </label>

              <span style={styles.coreBadge}>Unlimited</span>
            </div>

            <div style={styles.coreHelp}>
              Keep this board available as the persistent Core Board. Core Boards are limited to a maximum 4 × 6 grid.
            </div>

            {isCoreBoard && (
              <div style={styles.coreDefaultRow}>
                <label style={styles.coreFieldLabel} htmlFor="core-default-board">
                Default linked board
                </label>

                <select
                  id="core-default-board"
                  value={coreDefaultBoardId}
                  onChange={(e) => setCoreDefaultBoardId(e.target.value)}
                  style={styles.coreSelect}
                >
                  <option value="">No default — open Core Board full screen</option>
                  {(availableBoards || [])
                    .filter((board) => String(board.id) !== String(initialData.id || ''))
                    .map((board) => (
                      <option key={board.id} value={board.id}>
                        {board.name || `Board #${board.id}`}
                      </option>
                  ))}
                </select>

                <span style={styles.coreFieldHelp}>
                  When this Core Board is opened from the Boards page, the selected board will open automatically on the opposite side.
                </span>
              </div>
            )}
          </div>
        )}

        <div style={styles.wrapper}>
            <label>
              Columns <span title="Tiles across">↔️</span>:
            </label>
            <select
              value={cols}
              onChange={(e) => setCols(Number(e.target.value))}
              style={styles.select}
            >
              {[...Array(isCoreBoard ? 4 : 10)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>

              <label>
                Rows <span title="Tiles down">↕️</span>:
              </label>
              <select
                value={rows}
                onChange={(e) => setRows(Number(e.target.value))}
                style={styles.select}
              >
                {[...Array(isCoreBoard ? 6 : 10)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              
              <label>Theme:</label>
              <select
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                style={styles.select}
              >
                <option value="default">Default</option>
              </select>
            </div>
          
        <div style={styles.wrapper}>          
        {mode === 'edit' && (iTotalTiles > (cols * rows)) && (
        <div style={{ margin: '10px auto'}}>
              <span className="badge badge--warn" title="Tiles will need to be deleted.">Tile count: {cols * rows}. &#40;{iTotalTiles - (cols * rows)} tiles will be deleted!&#41;</span>
        </div>
        )}
        {mode === 'edit' && (iTotalTiles <= (cols * rows)) && (
        <div style={{ margin: '10px auto'}}>
              <span className="badge badge--ok" title="No tiles will need deleting.">Tile count: {cols * rows}</span>
        </div>    
        )}
        {mode !== 'edit' && (
        <div style={{ margin: '10px auto'}}>
              <span className="badge badge--ok" title="No tiles will need deleting.">Tile count: {cols * rows}</span>
        </div>    
        )}
        </div>
        <div style={styles.wrapper}>
          <div style={styles.checkboxRow}>
            <label>
              <input
                type="checkbox"
                checked={autoFit}
                onChange={() => setAutoFit(!autoFit)}
              />{' '}
              Autofit tiles (no scrolling)
            </label>
          </div>

          <div style={styles.checkboxRow}>
            <label>
              <input
                type="checkbox"
                checked={squareTiles}
                onChange={() => setSquareTiles(!squareTiles)}
              />{' '}
              Square tiles (equal width & height)
            </label>
          </div>
        </div>
        {/* Preview */}
        <div style={styles.previewContainer}>
          <div style={styles.previewLabel}>Preview:</div>

          <div
            style={{
              width: preview.frameWidth,
              height: preview.frameHeight,
              background: '#1878e9',
              borderRadius: 8,
              padding: 8,
              overflowY: preview.allowScrollY ? 'auto' : 'hidden',
              boxSizing: 'border-box',
              margin: 'auto',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${preview.columns}, ${preview.TILE_WIDTH}px)`,
                columnGap: `${preview.cTILE_GAP}px`,
                rowGap: `${preview.rTILE_GAP}px`,
                justifyContent: 'center',
                alignContent: 'start',
              }}
            >
              {Array.from({ length: preview.rows * preview.columns }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    width: `${preview.TILE_WIDTH}px`,
                    height: `${preview.TILE_HEIGHT}px`,
                    background: '#FFF',
                    borderRadius: 6,
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                  }}
                />
              ))}
            </div>
          </div>

          <div style={{ marginTop: 8, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>
            {autoFit
              ? 'Autofit ON — tiles scale to fill the frame (no vertical scrolling).'
              : 'Autofit OFF — tiles sized by width; vertical scrolling allowed.'}
          </div>
        </div>

        <div style={styles.buttons}>
          <button onClick={handleAdd} style={styles.addBtn}>
            {mode === 'edit' ? 'Save Changes' : 'Add Board'}
          </button>
          <button onClick={onClose} style={styles.cancelBtn}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    animation: 'fadeIn 0.2s ease-in',
  },
  wrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '15px',
    marginTop: '10px',
  },
  modal: {
    width: '85vw',
    height: '90vh',
    maxHeight: '650px',
    maxWidth: '750px',
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 12px 28px rgba(0,0,0,0.25)',
    padding: 20,
    animation: 'fadeIn 0.25s ease-in',
    overflowY: 'auto',
  },
  label: {
    display: 'block',
    textAlign: 'end',
    fontWeight: 600,
    marginTop: 10,
    marginBottom: 4,
  },  
  label1: {
    width: '20%',
    textAlign: 'end',
    display: 'block',
    fontWeight: 600,
    marginTop: 10,
    marginBottom: 4,
  },
  input: {
    width: '70%',
    padding: '8px',
    borderRadius: 6,
    border: '1px solid #ccc',
  },
  select: {
    padding: '6px',
    borderRadius: 6,
    border: '1px solid #ccc',
  },
  coreSection: {
    width: '90%',
    margin: '14px auto 4px',
    padding: '14px 16px',
    boxSizing: 'border-box',
    background: '#f7faff',
    border: '1px solid #d7e4f5',
    borderRadius: 10,
  },
  coreHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  coreToggleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    fontWeight: 700,
    fontSize: '15px',
    cursor: 'pointer',
  },
  coreCheckbox: {
    width: 18,
    height: 18,
    margin: 0,
    cursor: 'pointer',
    flexShrink: 0,
  },
  coreBadge: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: 999,
    background: '#e5f4ff',
    color: '#1261a0',
    fontSize: '11px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  coreHelp: {
    marginTop: 7,
    marginLeft: 27,
    color: '#5f6b76',
    fontSize: '12px',
    lineHeight: 1.45,
  },
  coreDefaultRow: {
    display: 'grid',
    gridTemplateColumns: '145px minmax(0, 1fr)',
    columnGap: 12,
    rowGap: 5,
    alignItems: 'center',
    marginTop: 14,
    paddingTop: 13,
    borderTop: '1px solid #d7e4f5',
  },
  coreFieldLabel: {
    fontWeight: 600,
    fontSize: '14px',
    textAlign: 'right',
  },
  coreSelect: {
    width: '100%',
    minWidth: 0,
    padding: '8px 10px',
    borderRadius: 6,
    border: '1px solid #c6cdd4',
    background: '#fff',
    boxSizing: 'border-box',
  },
  coreFieldHelp: {
    gridColumn: '2 / 3',
    color: '#66727e',
    fontSize: '11px',
    lineHeight: 1.4,
  },
  row: {
    display: 'flex',
    gap: 10,
    marginTop: 8,
  },
  checkboxRow: {
    marginTop: 10,
  },
  hint: {
    display: 'block',
    fontSize: '0.75rem',
    color: '#777',
    marginTop: 2,
  },
  previewContainer: {
    marginTop: 14,
  },
  previewLabel: {
    marginBottom: 6,
    fontWeight: 600,
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 25,
  },
  addBtn: {
    background: '#28a745',
    color: '#fff',
    border: 'none',
    padding: '8px 14px',
    borderRadius: 6,
    cursor: 'pointer',
  },
  cancelBtn: {
    background: '#6c757d',
    color: '#fff',
    border: 'none',
    padding: '8px 14px',
    borderRadius: 6,
    cursor: 'pointer',
  },
};

export default Popup;
